import * as React from "react";
import { Link } from "react-router-dom";
import { RouteEnum, UserRoleEnum } from "../requests/generated";

export const PAGES = {
  cases: "cases",
  protocols: "anesthetic protocols",
  management: "organization management",
  licenseManagement: "license management",
  accessLogs: "access logs",
  orderManagement: "order management",
  profile: "profile",
  emailLogs: "email logs",
  reports: "reports",
  settings: "settings"
};

export const APP_ROLES: { [key: string]: string } = {
  [UserRoleEnum.RoleSurgeon]: "Surgeon",
  [UserRoleEnum.RoleDvm]: "DVM",
  [UserRoleEnum.RoleDvmDavdc]: "DVM, DAVDC",
  [UserRoleEnum.RoleDoctor]: "Doctor",
  [UserRoleEnum.RoleAnesthesiologist]: "Anesthesiologist",
  [UserRoleEnum.RoleAnesthetist]: "Anesthetist",

  [UserRoleEnum.RoleNurse]: "Nurse",
  [UserRoleEnum.RoleLvt]: "LVT",
  [UserRoleEnum.RoleRvt]: "RVT",
  [UserRoleEnum.RoleCvt]: "CVT",
  [UserRoleEnum.RoleTechnician]: "Technician",
  [UserRoleEnum.RoleVeterinaryTechnician]: "Veterinary Technician",
  [UserRoleEnum.RoleAssistant]: "Assistant",

  [UserRoleEnum.RoleOrganizationAdmin]: "Organization Admin",
  [UserRoleEnum.RoleSuperAdmin]: "Super Admin"
};

export const SURGEON_ROLES = [
  UserRoleEnum.RoleSurgeon,
  UserRoleEnum.RoleDvm,
  UserRoleEnum.RoleDvmDavdc,
  UserRoleEnum.RoleDoctor,
  UserRoleEnum.RoleAnesthesiologist,
  UserRoleEnum.RoleAnesthetist
];

export const NURSE_ROLES = [
  UserRoleEnum.RoleNurse,
  UserRoleEnum.RoleLvt,
  UserRoleEnum.RoleRvt,
  UserRoleEnum.RoleCvt,
  UserRoleEnum.RoleTechnician,
  UserRoleEnum.RoleVeterinaryTechnician,
  UserRoleEnum.RoleAssistant
];

const generateType = (extensions: any) => {
  // TODO create string from back, BACK doesn't have validation for now
  const { types } = extensions.parameters;
  return `Image format is wrong. Allowed file formats: ${types} `;
};

interface Extensions {
  code: string;
  invalidValue: string | null;
  parameters: {
    orderId?: number;
    maxNumberOfUsers?: number;
  };
}

const generateEmail = (extensions: Extensions) => {
  const email = extensions.invalidValue;
  return `Email with instructions for recovering a password is sent to ${email}. If you have not received the letter, then check Spam. Your existing password has not been changed.`;
};

const generateUserLimitError = (extensions: Extensions) =>
  `Your license allows to give access only for ${extensions.parameters.maxNumberOfUsers} users. Please contact Admin of the system to expand your plan.`;

const generateExistingOrderError = (extensions: Extensions) => (
  <>
    This organization already has created order. Please modify it{" "}
    <Link
      style={{ textDecoration: "underline" }}
      to={`/order-management/${extensions.parameters.orderId}/edit`}
    >
      here
    </Link>{" "}
    or choose another organization.
  </>
);

export const ERROR_CODES = {
  validation_constraint_email_already_used_in_the_system:
    "This email is already used in the system.",
  validation_constraint_incorrect_login_or_password:
    "Login or Password is wrong. Please try again.",
  validation_constraint_organization_already_registered:
    "Such organization already exists.",
  validation_constraint_organization_with_same_name_already_exists:
    "Such organization already exists.",
  validation_constraint_user_cannot_login_user_is_inactive:
    "Login or Password is wrong. Please try again.",
  validation_constraint_user_cannot_request_password_recovery_user_is_inactive: generateEmail,
  validation_constraint_user_with_given_email_not_found:
    "Email is not correct or isn’t registered on the platform.",
  validation_constraint_password_recovery_token_expired:
    "Your Password Recovery link has been expired. Please request a new one.",
  validation_invalid_file_mime_type: generateType,
  validation_too_large_file: "Image size is too large. Max image size is 3MB.",
  validation_constraint_license_with_same_name_already_exists:
    "Such license is already created in the system.",
  validation_constraint_number_of_organization_users_exceeds_license_limit: generateUserLimitError,
  validation_constraint_cannot_login_organization_license_is_expired:
    "Your license is expired. Please contact Admin of the system to get access to the app.",
  validation_constraint_order_for_organization_already_exists: generateExistingOrderError,
  validation_constraint_cannot_save_order_license_is_not_valid:
    "Selected License is not valid. Please choose another License Type.",
  validation_constraint_registration_token_expired:
    "Your registration link has expired. Please contact Admin of the system.",
  validation_constraint_user_cannot_login_organization_is_archived:
    "Your account has been deactivated. Please contact the Organization Admin to activate it.",
  validation_constraint_invalid_api_key: "Invalid token."
};

export const EMAIL_REGEX = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

export const TOOTH_NUMBER_TO_TITLE: Record<string, string> = {
  101: "Upper Right 1st Incisor",
  102: "Upper Right 2nd Incisor",
  103: "Upper Right 3rd Incisor",
  104: "Upper Right Canine",
  105: "Upper Right 1st Pre-molar",
  106: "Upper Right 2nd Pre-molar",
  107: "Upper Right 3rd Pre-molar",
  108: "Upper Right 4th Pre-molar",
  109: "Upper Right 1st Molar",
  110: "Upper Right 2nd Molar",
  111: "Upper Right 3rd Molar",
  201: "Upper Left 1st Incisor",
  202: "Upper Left 2nd Incisor",
  203: "Upper Left 3rd Incisor",
  204: "Upper Left Canine",
  205: "Upper Left 1st Pre-molar",
  206: "Upper Left 2nd Pre-molar",
  207: "Upper Left 3rd Pre-molar",
  208: "Upper Left 4th Pre-molar",
  209: "Upper Left 1st Molar",
  210: "Upper Left 2nd Molar",
  211: "Upper Left 3rd Molar",
  301: "Lower Left 1st Incisor",
  302: "Lower Left 2nd Incisor",
  303: "Lower Left 3rd Incisor",
  304: "Lower Left Canine",
  305: "Lower Left 1st Pre-molar",
  306: "Lower Left 2nd Pre-molar",
  307: "Lower Left 3rd Pre-molar",
  308: "Lower Left 4th Pre-molar",
  309: "Lower Left 1st Molar",
  310: "Lower Left 2nd Molar",
  311: "Lower Left 3rd Molar",
  401: "Lower Right 1st Incisor",
  402: "Lower Right 2nd Incisor",
  403: "Lower Right 3rd Incisor",
  404: "Lower Right Canine",
  405: "Lower Right 1st Pre-molar",
  406: "Lower Right 2nd Pre-molar",
  407: "Lower Right 3rd Pre-molar",
  408: "Lower Right 4th Pre-molar",
  409: "Lower Right 1st Molar",
  410: "Lower Right 2nd Molar",
  411: "Lower Right 3rd Molar",
  501: "Upper Right 1st Incisor",
  502: "Upper Right 2nd Incisor",
  503: "Upper Right 3rd Incisor",
  504: "Upper Right Canine",
  506: "Upper Right 2nd Pre-molar",
  507: "Upper Right 3rd Pre-molar",
  508: "Upper Right 4th Pre-molar",
  601: "Upper Left 1st Incisor",
  602: "Upper Left 2nd Incisor",
  603: "Upper Left 3rd Incisor",
  604: "Upper Left Canine",
  606: "Upper Left 2nd Pre-molar",
  607: "Upper Left 3rd Pre-molar",
  608: "Upper Left 4th Pre-molar",
  701: "Lower Left 1st Incisor",
  702: "Lower Left 2nd Incisor",
  703: "Lower Left 3rd Incisor",
  704: "Lower Left Canine",
  706: "Lower Left 2nd Pre-molar",
  707: "Lower Left 3rd Pre-molar",
  708: "Lower Left 4th Pre-molar",
  801: "Lower Right 1st Incisor",
  802: "Lower Right 2nd Incisor",
  803: "Lower Right 3rd Incisor",
  804: "Lower Right Canine",
  806: "Lower Right 2nd Pre-molar",
  807: "Lower Right 3rd Pre-molar",
  808: "Lower Right 4th Pre-molar"
};

export const CAT_ADULT_TEETH = [
  101,
  102,
  103,
  104,
  106,
  107,
  108,
  109,

  201,
  202,
  203,
  204,
  206,
  207,
  208,
  209,

  301,
  302,
  303,
  304,
  307,
  308,
  309,

  401,
  402,
  403,
  404,
  407,
  408,
  409
];

export const CAT_MILK_TEETH = [
  501,
  502,
  503,
  504,
  506,
  507,
  508,

  601,
  602,
  603,
  604,
  606,
  607,
  608,

  701,
  702,
  703,
  704,
  707,
  708,

  801,
  802,
  803,
  804,
  807,
  808
];

export const DOG_ADULT_TEETH = [
  101,
  102,
  103,
  104,
  105,
  106,
  107,
  108,
  109,
  110,

  201,
  202,
  203,
  204,
  205,
  206,
  207,
  208,
  209,
  210,

  301,
  302,
  303,
  304,
  305,
  306,
  307,
  308,
  309,
  310,
  311,

  401,
  402,
  403,
  404,
  405,
  406,
  407,
  408,
  409,
  410,
  411
];

export const DOG_MILK_TEETH = [
  501,
  502,
  503,
  504,
  506,
  507,
  508,

  601,
  602,
  603,
  604,
  606,
  607,
  608,

  701,
  702,
  703,
  704,
  706,
  707,
  708,

  801,
  802,
  803,
  804,
  806,
  807,
  808
];

export const RABBIT_TEETH = [
  101,
  102,
  106,
  107,
  108,
  109,
  110,
  111,

  201,
  202,
  206,
  207,
  208,
  209,
  210,
  211,

  301,
  307,
  308,
  309,
  310,
  311,

  401,
  407,
  408,
  409,
  410,
  411
];

export const ROUTE_OPTIONS = [
  { value: RouteEnum.Im, label: "IM" },
  { value: RouteEnum.Iv, label: "IV" },
  { value: RouteEnum.Sc, label: "SC" },
  { value: RouteEnum.Po, label: "PO" },
  { value: RouteEnum.Td, label: "TD" }
];

export const DOSE_GKg = "1";
export const DOSE_MgKg = "2";
export const DOSE_MugKg = "3";

export const DOSE_UNIT_OPTIONS = [
  { value: DOSE_GKg, label: "g/kg" },
  { value: DOSE_MgKg, label: "mg/kg" },
  { value: DOSE_MugKg, label: "µg/kg" }
];

export const DENTAL_PROCEDURES = [
  "Comprehensive Oral Health Assessment and Treatment (Dental Cleaning)",
  "Crown",
  "Root canal therapy",
  "Oral radiographs"
];
export const TPLO_PROCEDURE = "Tibial plateau leveling osteotomy (TPLO)";

export const ORGANIZATION_TABS = {
  general: {
    id: 0,
    to: "general",
    name: "General"
  },
  admin: {
    id: 1,
    to: "admin",
    name: "Organization Admin"
  },
  users: {
    id: 2,
    to: "users",
    name: "Users"
  }
};

export const SETTINGS_TABS = {
  general: {
    id: 0,
    to: "general",
    name: "General"
  },
  "check-in": {
    id: 1,
    to: "check-in",
    name: "Check-in settings"
  },
  integrations: {
    id: 2,
    to: "integrations",
    name: "Integrations"
  }
};

export interface DeserializeElement {
  [key: string]: (
    el: Node
  ) => {
    type: string;
  };
}

export const ELEMENT_TAGS: DeserializeElement = {
  LI: () => ({ type: "list-item" }),
  OL: () => ({ type: "numbered-list" }),
  P: () => ({ type: "paragraph" }),
  UL: () => ({ type: "bulleted-list" })
};

interface DeserializeLeaf {
  [key: string]: (
    el: Node
  ) => {
    [key: string]: any;
  };
}

export const TEXT_TAGS: DeserializeLeaf = {
  EM: () => ({ italic: true }),
  I: () => ({ italic: true }),
  STRONG: () => ({ bold: true }),
  B: () => ({ bold: true }),
  U: () => ({ underline: true })
};

export const DRAWING_TOOL_COLORS: { name: string; rgba: string }[] = [
  { name: "black", rgba: "rgba(0, 0, 0, 0.35)" },
  { name: "red", rgba: "rgba(255, 0, 0, 0.35)" },
  { name: "gray", rgba: "rgba(128, 128, 128, 0.35)" },
  { name: "green", rgba: "rgba(0, 128, 0, 0.35)" },
  { name: "blue", rgba: "rgba(0, 0, 255, 0.35)" },
  { name: "lightblue", rgba: "rgba(173, 216, 230, 0.35)" },
  { name: "yellow", rgba: "rgba(255, 255, 0, 0.35)" },
  { name: "orange", rgba: "rgba(255, 165, 0, 0.35)" },
  { name: "purple", rgba: "rgba(128, 0, 128, 0.35)" },
  { name: "pink", rgba: "rgba(255, 192, 203, 0.35)" }
];

export const DEEP_POCKET_VALUE_FOR_DOG = 4;

export const DEEP_POCKET_VALUE_FOR_CAT_AND_RABBIT = 2;

export const BONE_LOSS_KEYS = [
  "between25And50PerCentBoneLoss",
  "externalCervicalToothResorption",
  "externalInflammatoryToothResorption",
  "externalReplacementRootResorption",
  "externalSurfaceRootResorption",
  "greaterThan50PerCentBoneLoss",
  "internalToothResorption",
  "lessThan25PerCentBoneLoss",
  "missingTeeth",
  "periapicalLucency",
  "periapicalRarefaction",
  "retainedToothRoots",
  "toothResorptionType1",
  "toothResorptionType2",
  "toothResorptionType3",
  "uneruptedTeeth",
  "widenedPulpCanal"
];
